import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  BAZZEF_QR_THEMES,
  buildBazzefQrUrl,
  renderBazzefQrCanvas,
} from '../utils/bazzefQr';
import { getMediaUrl } from '../utils/mediaUrl';
import { triggerHaptic, nativeShare } from '../utils/nativeBridge';
import { saveMediaToDevice } from '../utils/mediaCache';
import { useToast } from '../contexts/ToastContext';
import BazzefQrScanner from './BazzefQrScanner';

/**
 * Premium Bazzef QR Code Modal ("Nametag / Bazzcode").
 *
 * Displays a branded, customizable QR code that other users can scan to instantly
 * start a message or follow the account. Also includes an integrated scanner tab.
 */
const BazzefQrModal = ({
  isOpen,
  onClose,
  user,
  initialTab = 'code', // 'code' | 'scan'
}) => {
  const toast = useToast();
  const canvasRef = useRef(null);
  const avatarImgRef = useRef(null);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedTheme, setSelectedTheme] = useState('classic');
  const [isGenerating, setIsGenerating] = useState(true);

  // Sync tab if initialTab changes
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  const qrUrl = user ? buildBazzefQrUrl(user) : '';
  const currentTheme = BAZZEF_QR_THEMES[selectedTheme] || BAZZEF_QR_THEMES.classic;

  // Render QR Canvas whenever user, theme, or open state changes
  useEffect(() => {
    if (!isOpen || activeTab !== 'code' || !qrUrl || !canvasRef.current) return;

    let isCancelled = false;
    setIsGenerating(true);

    const render = async () => {
      const avatarSrc = user.profilePicture || user.profile_picture
        ? getMediaUrl(user.profilePicture || user.profile_picture)
        : null;

      await renderBazzefQrCanvas(canvasRef.current, qrUrl, {
        theme: selectedTheme,
        size: 512,
        avatarSrc,
      });

      if (!isCancelled) {
        setIsGenerating(false);
      }
    };

    render();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, activeTab, qrUrl, selectedTheme, user]);

  // Share QR Code
  const handleShare = async () => {
    triggerHaptic('light');
    const title = `${user?.username || 'User'} on Bazzef`;
    const text = `Connect with @${user?.handle || user?.username} on Bazzef!`;

    const shared = await nativeShare({
      title,
      text,
      url: qrUrl,
      dialogTitle: 'Share Bazzef Profile',
    });

    if (!shared && navigator.clipboard) {
      await navigator.clipboard.writeText(qrUrl);
      toast.success('Profile link copied to clipboard!');
    }
  };

  // Download / Save QR Code Card Image to user's device
  const handleSaveImage = async () => {
    if (!canvasRef.current) return;
    triggerHaptic('medium');

    try {
      const blob = await new Promise((resolve) =>
        canvasRef.current.toBlob(resolve, 'image/png')
      );
      if (!blob) throw new Error('Could not export canvas to blob');

      const filename = `bazzef_qr_${user?.username || 'code'}.png`;
      const ok = await saveMediaToDevice(URL.createObjectURL(blob), filename);
      if (ok) {
        toast.success('Bazzef QR Code saved to device photos!');
      } else {
        toast.error('Failed to save QR code');
      }
    } catch (err) {
      toast.error('Could not save QR code image');
    }
  };

  if (!isOpen) return null;

  // If scanner tab is selected, render the full-screen camera scanner
  if (activeTab === 'scan') {
    return (
      <BazzefQrScanner
        isOpen={isOpen}
        onClose={onClose}
        onSwitchToCode={() => {
          triggerHaptic('light');
          setActiveTab('code');
        }}
        onUserScanned={() => {
          // Keep scanner active or close as handled
        }}
      />
    );
  }

  const modalContent = (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 select-none animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-sm rounded-3xl bg-gradient-to-b ${currentTheme.cardBg} border ${currentTheme.accentBorder} shadow-2xl p-5 flex flex-col items-center gap-4 text-white overflow-hidden transition-all duration-300`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Background Ambient Glow */}
        <div
          className="absolute -top-24 -left-24 w-48 h-48 rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ backgroundColor: currentTheme.startColor }}
        />
        <div
          className="absolute -bottom-24 -right-24 w-48 h-48 rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ backgroundColor: currentTheme.endColor }}
        />

        {/* Top Controls Header */}
        <div className="w-full flex items-center justify-between z-10">
          {/* Tab Switcher: My Code / Scan */}
          <div className="flex items-center p-1 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs font-bold">
            <button
              type="button"
              onClick={() => {
                triggerHaptic('light');
                setActiveTab('code');
              }}
              className={`px-3 py-1.5 rounded-full transition-all ${
                activeTab === 'code'
                  ? 'bg-white text-zinc-950 shadow-sm'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              My Code
            </button>
            <button
              type="button"
              onClick={() => {
                triggerHaptic('light');
                setActiveTab('scan');
              }}
              className={`px-3 py-1.5 rounded-full transition-all flex items-center gap-1.5 ${
                activeTab === 'scan'
                  ? 'bg-white text-zinc-950 shadow-sm'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              <i className="fas fa-camera text-[10px]" />
              <span>Scan</span>
            </button>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all active:scale-95"
            aria-label="Close"
          >
            <i className="fas fa-times text-sm" />
          </button>
        </div>

        {/* Brand Crest */}
        <div className="flex items-center gap-2 z-10 mt-1">
          <div className="w-6 h-6 rounded-lg bg-gradient-to-tr from-indigo-500 to-violet-500 flex items-center justify-center font-black text-xs shadow-md">
            B
          </div>
          <span className="font-extrabold tracking-widest text-xs uppercase text-white/90">
            BAZZEF CONNECT
          </span>
        </div>

        {/* The Branded QR Code Canvas Card */}
        <div className="relative z-10 w-64 h-64 sm:w-72 sm:h-72 p-3 bg-white rounded-3xl shadow-2xl flex items-center justify-center overflow-hidden border-2 border-white/40 ring-4 ring-black/10">
          <canvas
            ref={canvasRef}
            className={`w-full h-full object-contain rounded-2xl transition-opacity duration-200 ${
              isGenerating ? 'opacity-20' : 'opacity-100'
            }`}
          />
          {isGenerating && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {/* User Card Badge */}
        <div className="flex items-center gap-3 z-10 bg-black/40 backdrop-blur-md px-4 py-2 rounded-2xl border border-white/10 w-full">
          {user?.profilePicture || user?.profile_picture ? (
            <img
              src={getMediaUrl(user.profilePicture || user.profile_picture)}
              alt={user.username}
              className="w-10 h-10 rounded-full object-cover border border-white/30"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-indigo-500 to-violet-500 flex items-center justify-center font-bold text-sm">
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <h3 className="font-extrabold text-sm truncate">{user?.username}</h3>
              {user?.isVerified && (
                <i className="fas fa-circle-check text-sky-400 text-[11px]" />
              )}
            </div>
            <p className="text-white/60 text-xs truncate">
              @{user?.handle || user?.username}
            </p>
          </div>
        </div>

        {/* Theme Picker Palette */}
        <div className="flex items-center justify-center gap-2 z-10 py-0.5">
          {Object.values(BAZZEF_QR_THEMES).map((th) => (
            <button
              key={th.id}
              type="button"
              onClick={() => {
                triggerHaptic('light');
                setSelectedTheme(th.id);
              }}
              title={th.name}
              aria-label={th.name}
              className={`w-6 h-6 rounded-full transition-transform ${
                selectedTheme === th.id
                  ? 'ring-2 ring-white scale-125'
                  : 'opacity-70 hover:opacity-100'
              }`}
              style={{
                background: `linear-gradient(135deg, ${th.startColor}, ${th.endColor})`,
              }}
            />
          ))}
        </div>

        {/* Action Buttons: Save to Device & Share */}
        <div className="grid grid-cols-2 gap-2.5 w-full z-10 pt-1">
          <button
            type="button"
            onClick={handleSaveImage}
            className="h-10 rounded-xl bg-white/15 hover:bg-white/25 border border-white/20 text-white font-bold text-xs flex items-center justify-center gap-2 backdrop-blur-md active:scale-98 transition-all"
          >
            <i className="fas fa-arrow-down-to-bracket text-xs" />
            <span>Save Image</span>
          </button>
          <button
            type="button"
            onClick={handleShare}
            className="h-10 rounded-xl bg-white text-zinc-950 font-bold text-xs flex items-center justify-center gap-2 shadow-lg hover:bg-zinc-100 active:scale-98 transition-all"
          >
            <i className="fas fa-share-nodes text-xs" />
            <span>Share</span>
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
};

export default React.memo(BazzefQrModal);
